'use client'

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useField, useFormFields } from '@payloadcms/ui'
import type { JSONFieldClientComponent } from 'payload'
import {
  HOME_BOXES,
  HOME_POOL_SIZE,
  normalizeSlots,
  pinToBox,
  rankPosts,
  releaseBox,
  resolveHomeBoxes,
  swapBoxes,
  type HomeSlot,
  type ResolvedBox,
} from '../../lib/homeLayout'
import { relationId } from '../../lib/relations'
import { errMessage } from '../../lib/errors'

/**
 * LO SCHELETRO DELLA HOME, nell'admin: i box numerati a sinistra, gli articoli a destra.
 *
 * È il Field del campo json `homepageLayout.slots` (vedi collections/HomepageLayout.ts): l'editor
 * scrive nello stato del form, il salvataggio è il normale pulsante «Salva» del documento. Nessun
 * endpoint dedicato, nessuna scrittura fuori dal ciclo di Payload — quindi versioni, permessi e
 * avviso di modifiche non salvate valgono anche qui.
 *
 * PERCHÉ L'ANTEPRIMA È ATTENDIBILE. I riquadri non sono un disegno: sono il risultato di
 * `resolveHomeBoxes`, la stessa funzione che la home chiama per rendere la pagina
 * (src/lib/homeLayout.ts). Cambiando un pin qui la disposizione si ricalcola con la stessa regola
 * che girerà in produzione, compreso l'effetto di rimbalzo sui box automatici a valle — che è
 * l'unica cosa che conta davvero capire prima di salvare.
 *
 * Il bacino da cui pesca l'automatico sono i primi HOME_POOL_SIZE articoli per data, cioè
 * esattamente quelli che legge la home. La lista di destra ne mostra di più (si può fissare anche
 * un pezzo vecchio) e per quelli fuori bacino c'è una seconda lettura per id.
 */

const CANDIDATE_LIMIT = 60

type MediaLike = {
  url?: string | null
  sizes?: Record<string, { url?: string | null } | undefined> | null
}

type Candidate = {
  id: number
  title?: string | null
  publishedAt?: string | null
  createdAt?: string | null
  placement?: (string | null)[] | null
  featuredImage?: MediaLike | number | null
}

type Pending = { kind: 'box'; n: number } | { kind: 'post'; id: number } | null

const SELECT_FIELDS = ['title', 'publishedAt', 'placement', 'featuredImage'] as const

const thumbOf = (post: Candidate): string | null => {
  const img = post.featuredImage
  if (!img || typeof img !== 'object') return null
  const sizes = img.sizes ?? {}
  return sizes['small-thumb']?.url || sizes['medium-thumb']?.url || img.url || null
}

const dateLabel = (post: Candidate): string => {
  const raw = post.publishedAt || post.createdAt
  if (!raw) return 'senza data'
  const d = new Date(raw)
  if (Number.isNaN(d.getTime())) return 'senza data'
  return d.toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' })
}

/** I `placement` valorizzati, in italiano: dicono perché un articolo è più su di quanto la data direbbe. */
const PLACEMENT_LABEL: Record<string, string> = {
  featured_top: 'in evidenza',
  center_top: 'centro alto',
  center_bottom: 'centro basso',
  right_side: 'colonna destra',
  footer_center: 'pre-footer',
}
const placementLabel = (post: Candidate): string | null => {
  const values = (post.placement ?? []).filter((v): v is string => Boolean(v))
  const labels = values.map((v) => PLACEMENT_LABEL[v]).filter(Boolean)
  return labels.length ? labels.join(', ') : null
}

/** Query REST per gli articoli: leggera (solo i campi che servono) e SEMPRE scoped sul tenant. */
const postsQuery = (tenantId: string, extra: Record<string, string>): string => {
  const params = new URLSearchParams({
    depth: '1',
    sort: '-publishedAt',
    'where[status][equals]': 'published',
    // Lo scoping esplicito non è ridondante: un super-admin ha accesso a tutti i tenant, quindi
    // senza questo filtro la lista di una testata potrebbe mostrare gli articoli dell'altra.
    'where[tenant][equals]': tenantId,
    ...extra,
  })
  for (const f of SELECT_FIELDS) params.append(`select[${f}]`, 'true')
  return `/api/posts?${params.toString()}`
}

const HomepageLayoutEditor: JSONFieldClientComponent = ({ field, path, readOnly }) => {
  const { setValue, value } = useField<HomeSlot[]>({ path })
  const slots = useMemo(() => normalizeSlots(value), [value])

  // Il tenant del documento. Su una riga mai salvata il campo è ancora vuoto: si ripiega sul
  // cookie del selettore di sito, che è la stessa fonte che userebbe Payload al salvataggio.
  const tenantField = useFormFields(([fields]) => fields?.tenant?.value)
  const tenantId = useMemo(() => {
    const fromField = relationId(tenantField)
    if (fromField != null && fromField !== '') return String(fromField)
    if (typeof document === 'undefined') return ''
    return document.cookie.match(/(?:^|;\s*)payload-tenant=([^;]+)/)?.[1] ?? ''
  }, [tenantField])

  const [pool, setPool] = useState<Candidate[]>([])
  const [outsiders, setOutsiders] = useState<Candidate[]>([])
  const [fetching, setFetching] = useState(true)
  const [fetchError, setFetchError] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [pending, setPending] = useState<Pending>(null)
  const [dropTarget, setDropTarget] = useState<number | null>(null)

  // «Adesso» congelato al montaggio: la finestra delle promozioni (72h) entra nel calcolo, e un
  // Date.now() dentro il memo farebbe ballare l'anteprima a ogni render senza motivo.
  const [now] = useState(() => Date.now())
  const askedIds = useRef(new Set<number>())

  // Cambiare sito riapre il caricamento da capo. L'aggiustamento si fa DURANTE il render,
  // confrontando col sito dell'ultimo render: in un effect sarebbe un render in più, e per un
  // frame si vedrebbero gli articoli del sito precedente sotto l'intestazione di quello nuovo.
  const [lastTenant, setLastTenant] = useState(tenantId)
  if (tenantId !== lastTenant) {
    setLastTenant(tenantId)
    setFetching(true)
    setFetchError(null)
  }

  // Senza sito non c'è niente da caricare, e il messaggio non è uno stato da scrivere: si legge
  // da `tenantId`. Prima stava in due setState sincroni dentro l'effect qui sotto.
  const loading = Boolean(tenantId) && fetching
  const error = tenantId ? fetchError : 'Nessun sito selezionato: scegli il sito dal selettore in alto.'

  useEffect(() => {
    if (!tenantId) return
    let alive = true
    fetch(postsQuery(tenantId, { limit: String(CANDIDATE_LIMIT) }), { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`))))
      .then((data: { docs?: Candidate[] }) => {
        if (!alive) return
        setPool(data.docs ?? [])
        setFetching(false)
      })
      .catch((e: unknown) => {
        if (!alive) return
        setFetchError(`Non riesco a leggere gli articoli (${errMessage(e)}).`)
        setFetching(false)
      })
    return () => {
      alive = false
    }
  }, [tenantId])

  // Gli articoli fissati che non stanno tra i più recenti (un evergreen in apertura): seconda
  // lettura per id. `askedIds` evita il ciclo infinito su un pin morto, che non tornerà mai.
  useEffect(() => {
    if (!tenantId || loading) return
    const known = new Set([...pool, ...outsiders].map((p) => p.id))
    const missing = slots.map((s) => s.post).filter((id) => !known.has(id) && !askedIds.current.has(id))
    if (!missing.length) return
    missing.forEach((id) => askedIds.current.add(id))
    const params: Record<string, string> = { limit: String(missing.length) }
    missing.forEach((id, i) => {
      params[`where[id][in][${i}]`] = String(id)
    })
    fetch(postsQuery(tenantId, params), { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`))))
      .then((data: { docs?: Candidate[] }) => setOutsiders((prev) => [...prev, ...(data.docs ?? [])]))
      .catch(() => {
        /* un pin morto resta segnalato come tale: nessun rumore in console */
      })
  }, [slots, pool, outsiders, tenantId, loading])

  const resolved = useMemo(
    () => resolveHomeBoxes<Candidate>({ posts: pool.slice(0, HOME_POOL_SIZE), pinned: outsiders, slots, now }),
    [pool, outsiders, slots, now],
  )

  const boxOfPost = useMemo(() => {
    const map = new Map<number, number>()
    resolved.forEach((r) => {
      if (r.post) map.set(r.post.id, r.box.n)
    })
    return map
  }, [resolved])

  const apply = useCallback(
    (next: HomeSlot[]) => {
      setValue(next)
      setPending(null)
    },
    [setValue],
  )

  const pin = useCallback((box: number, postId: number) => apply(pinToBox(slots, box, postId)), [apply, slots])
  const release = useCallback((box: number) => apply(releaseBox(slots, box)), [apply, slots])
  const swap = useCallback(
    (a: number, b: number) => apply(swapBoxes(slots, resolved, a, b)),
    [apply, slots, resolved],
  )

  /** Un click su un box: chiude un'azione in sospeso, o la apre. */
  const onBoxClick = (n: number) => {
    if (readOnly) return
    if (!pending) return setPending({ kind: 'box', n })
    if (pending.kind === 'post') return pin(n, pending.id)
    if (pending.n === n) return setPending(null)
    return swap(pending.n, n)
  }

  const onPostClick = (id: number) => {
    if (readOnly) return
    if (pending?.kind === 'box') return pin(pending.n, id)
    setPending((p) => (p?.kind === 'post' && p.id === id ? null : { kind: 'post', id }))
  }

  const onDrop = (n: number, raw: string) => {
    setDropTarget(null)
    if (readOnly) return
    const [kind, rawId] = raw.split(':')
    const id = Number(rawId)
    if (!Number.isInteger(id)) return
    if (kind === 'post') pin(n, id)
    else if (kind === 'box') swap(id, n)
  }

  const pinnedCount = slots.length
  const leadPinned = resolved[0]?.pinned === true
  const ranked = useMemo(() => rankPosts([...pool, ...outsiders], now), [pool, outsiders, now])
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return ranked
    return ranked.filter((p) => (p.title ?? '').toLowerCase().includes(q))
  }, [ranked, query])

  const renderTile = (r: ResolvedBox<Candidate>) => {
    const n = r.box.n
    const post = r.post
    const thumb = post ? thumbOf(post) : null
    const isPending = pending?.kind === 'box' && pending.n === n
    const classes = [
      'hle-tile',
      r.pinned ? 'is-pinned' : '',
      isPending ? 'is-pending' : '',
      dropTarget === n ? 'is-drop' : '',
      !post ? 'is-empty' : '',
    ]
      .filter(Boolean)
      .join(' ')

    return (
      <div
        key={n}
        className={classes}
        draggable={!readOnly && Boolean(post)}
        onClick={() => onBoxClick(n)}
        onDragStart={(e) => e.dataTransfer.setData('text/plain', `box:${n}`)}
        onDragOver={(e) => {
          e.preventDefault()
          setDropTarget(n)
        }}
        onDragLeave={() => setDropTarget((t) => (t === n ? null : t))}
        onDrop={(e) => {
          e.preventDefault()
          onDrop(n, e.dataTransfer.getData('text/plain'))
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onBoxClick(n)
          }
        }}
        role="button"
        tabIndex={0}
        title={`Box ${n} · ${r.box.label}\n${r.box.hint}`}
        aria-label={`Box ${n}, ${r.box.label}. ${post?.title ?? 'vuoto'}. ${r.pinned ? 'Fissato' : 'Automatico'}`}
      >
        {thumb ? <img alt="" className="hle-thumb" src={thumb} /> : <div className="hle-thumb hle-thumb--none" />}
        <span className="hle-num">{n}</span>
        <span className={`hle-chip ${r.pinned ? 'is-pinned' : ''}`}>{r.pinned ? 'Fissato' : 'Auto'}</span>
        <div className="hle-tile-body">
          <strong className="hle-tile-title">{post?.title ?? '— nessun articolo —'}</strong>
          <span className="hle-tile-meta">{r.box.label}</span>
        </div>
        {r.danglingPin != null ? (
          <span className="hle-warn" title={`L'articolo fissato (id ${r.danglingPin}) non è più pubblicato: il box è tornato automatico.`}>
            pin morto
          </span>
        ) : null}
        {!readOnly ? (
          <div className="hle-acts" onClick={(e) => e.stopPropagation()}>
            <button
              aria-label={`Sposta il box ${n} indietro`}
              className="hle-act"
              disabled={n === 1}
              onClick={() => swap(n, n - 1)}
              title="Scambia col box precedente"
              type="button"
            >
              ←
            </button>
            <button
              aria-label={`Sposta il box ${n} avanti`}
              className="hle-act"
              disabled={n === HOME_BOXES.length}
              onClick={() => swap(n, n + 1)}
              title="Scambia col box successivo"
              type="button"
            >
              →
            </button>
            <button
              aria-label={`Libera il box ${n}`}
              className="hle-act"
              disabled={!r.pinned && r.danglingPin == null}
              onClick={() => release(n)}
              title="Torna automatico"
              type="button"
            >
              ✕
            </button>
          </div>
        ) : null}
      </div>
    )
  }

  const mosaic = resolved.filter((r) => r.box.zone === 'mosaic')
  const grid = resolved.filter((r) => r.box.zone === 'grid')

  return (
    <div className="field-type hle-wrap">
      <style>{CSS}</style>

      <div className="hle-head">
        <div>
          <h3 className="hle-h3">{typeof field.label === 'string' ? field.label : 'Box della home'}</h3>
          <p className="hle-lead">
            Trascina un articolo dalla lista su un box, oppure clicca prima l&apos;uno e poi l&apos;altro. Trascinando
            un box su un altro i due si scambiano. I box <em>automatici</em> si riempiono da soli con gli articoli più
            recenti: quelli <em>fissati</em> restano fermi anche quando esce un pezzo nuovo.
          </p>
        </div>
        <div className="hle-head-actions">
          <span className="hle-count">
            {pinnedCount} {pinnedCount === 1 ? 'box fissato' : 'box fissati'} su {HOME_BOXES.length}
          </span>
          <button
            className="hle-btn"
            disabled={readOnly || pinnedCount === 0}
            onClick={() => apply([])}
            type="button"
          >
            Sblocca tutti
          </button>
        </div>
      </div>

      {leadPinned ? (
        <p className="hle-note">
          Il box 1 è fissato: gli articoli pubblicati da qui in avanti non andranno in apertura finché non lo liberi.
        </p>
      ) : null}
      {error ? <p className="hle-error">{error}</p> : null}
      {pending ? (
        <p className="hle-pending">
          {pending.kind === 'box'
            ? `Box ${pending.n} selezionato: clicca un articolo per metterlo qui, o un altro box per scambiarli.`
            : 'Articolo selezionato: clicca il box in cui metterlo.'}
          <button className="hle-btn hle-btn--ghost" onClick={() => setPending(null)} type="button">
            annulla
          </button>
        </p>
      ) : null}

      <div className="hle-cols">
        <section>
          <h4 className="hle-zone">In evidenza (mosaico)</h4>
          <div className="hle-mosaic">
            <div className="hle-lead">{mosaic[0] ? renderTile(mosaic[0]) : null}</div>
            <div className="hle-side">{mosaic.slice(1).map(renderTile)}</div>
          </div>

          <h4 className="hle-zone">Ultimi articoli (griglia)</h4>
          <div className="hle-grid">{grid.map(renderTile)}</div>
          <p className="hle-foot">
            Sotto la griglia la home continua con le sezioni per categoria, che restano automatiche.
          </p>
        </section>

        <aside className="hle-aside">
          <label className="hle-search-wrap">
            <span className="sr-only hle-sr">Cerca un articolo</span>
            <input
              className="hle-search"
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cerca un articolo…"
              type="search"
              value={query}
            />
          </label>

          {loading ? <p className="hle-foot">Carico gli articoli…</p> : null}

          <ul className="hle-list">
            {filtered.map((post) => {
              const box = boxOfPost.get(post.id)
              const isPinned = slots.some((s) => s.post === post.id)
              const isPending = pending?.kind === 'post' && pending.id === post.id
              const thumb = thumbOf(post)
              const where = placementLabel(post)
              return (
                <li key={post.id}>
                  <div
                    className={`hle-row ${isPending ? 'is-pending' : ''} ${box ? 'is-live' : ''}`}
                    draggable={!readOnly}
                    onClick={() => onPostClick(post.id)}
                    onDragStart={(e) => e.dataTransfer.setData('text/plain', `post:${post.id}`)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        onPostClick(post.id)
                      }
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    {thumb ? <img alt="" className="hle-row-thumb" src={thumb} /> : <span className="hle-row-thumb" />}
                    <span className="hle-row-text">
                      <strong className="hle-row-title">{post.title ?? `articolo ${post.id}`}</strong>
                      <span className="hle-row-meta">
                        {dateLabel(post)}
                        {where ? ` · ${where}` : ''}
                      </span>
                    </span>
                    <span className={`hle-row-box ${isPinned ? 'is-pinned' : ''}`}>
                      {box ? `Box ${box}` : 'fuori home'}
                    </span>
                  </div>
                </li>
              )
            })}
          </ul>
          {!loading && !filtered.length ? <p className="hle-foot">Nessun articolo pubblicato.</p> : null}
          <p className="hle-foot">
            La lista è nell&apos;ordine con cui i box automatici pescano: chi sta in alto entra per primo quando un box
            si libera.
          </p>
        </aside>
      </div>
    </div>
  )
}

export default HomepageLayoutEditor

/**
 * CSS in un tag <style> invece che negli style inline dei nodi: qui servono :hover, :focus-visible
 * e una media query (a schermo strette le due colonne diventano una), e nessuna delle tre si può
 * scrivere inline. I nomi sono prefissati `hle-` per non collidere con i fogli dell'admin.
 */
const CSS = `
.hle-wrap { display: grid; gap: .75rem; }
.hle-head { display: flex; flex-wrap: wrap; gap: .75rem; align-items: flex-start; justify-content: space-between; }
.hle-h3 { margin: 0 0 .25rem; font-size: 1rem; }
.hle-lead { margin: 0; max-width: 62ch; font-size: .8rem; line-height: 1.5; color: var(--theme-elevation-600); }
.hle-head-actions { display: flex; align-items: center; gap: .5rem; }
.hle-count { font-size: .75rem; color: var(--theme-elevation-600); white-space: nowrap; }
.hle-btn { font-size: .75rem; padding: .3rem .6rem; border-radius: var(--style-radius-s); cursor: pointer;
  border: 1px solid var(--theme-elevation-250); background: var(--theme-elevation-50); color: var(--theme-elevation-800); }
.hle-btn:hover:not(:disabled) { background: var(--theme-elevation-100); }
.hle-btn:disabled { opacity: .4; cursor: default; }
.hle-btn--ghost { border-color: transparent; background: transparent; text-decoration: underline; margin-left: .5rem; }
.hle-note, .hle-error, .hle-pending { margin: 0; font-size: .78rem; line-height: 1.45; padding: .45rem .6rem;
  border-radius: var(--style-radius-s); border: 1px solid var(--theme-elevation-150); background: var(--theme-elevation-50); }
.hle-note { border-color: var(--theme-warning-250); background: var(--theme-warning-50); }
.hle-error { border-color: var(--theme-error-250); background: var(--theme-error-50); }
.hle-pending { border-color: var(--theme-success-250); background: var(--theme-success-50); }
.hle-cols { display: grid; grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr); gap: 1rem; align-items: start; }
@media (max-width: 1100px) { .hle-cols { grid-template-columns: minmax(0, 1fr); } }
.hle-zone { margin: .5rem 0 .35rem; font-size: .72rem; text-transform: uppercase; letter-spacing: .06em;
  color: var(--theme-elevation-500); }
.hle-mosaic { display: grid; grid-template-columns: 2fr 1fr; gap: .5rem; }
.hle-lead { display: grid; }
.hle-lead .hle-tile { aspect-ratio: 16 / 10; }
.hle-side { display: flex; flex-direction: column; gap: .5rem; }
.hle-side .hle-tile { flex: 1 1 0; min-height: 0; }
.hle-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: .5rem; }
.hle-grid .hle-tile { aspect-ratio: 16 / 10; }

.hle-tile { position: relative; overflow: hidden; border-radius: var(--style-radius-s);
  border: 1px solid var(--theme-elevation-200); background: var(--theme-elevation-100); cursor: grab; }
.hle-tile:focus-visible { outline: 2px solid var(--theme-success-500); outline-offset: 2px; }
.hle-tile.is-pinned { border-color: var(--theme-success-500); box-shadow: inset 0 0 0 1px var(--theme-success-500); }
.hle-tile.is-pending { outline: 2px solid var(--theme-success-500); outline-offset: 1px; }
.hle-tile.is-drop { outline: 2px dashed var(--theme-success-500); outline-offset: 1px; }
.hle-tile.is-empty { background: repeating-linear-gradient(45deg, var(--theme-elevation-50),
  var(--theme-elevation-50) 6px, var(--theme-elevation-100) 6px, var(--theme-elevation-100) 12px); }
.hle-thumb { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
.hle-thumb--none { background: var(--theme-elevation-150); }
.hle-tile::after { content: ''; position: absolute; inset: 0;
  background: linear-gradient(to top, rgba(0,0,0,.82) 0%, rgba(0,0,0,.35) 45%, rgba(0,0,0,.05) 100%); }
.hle-num { position: absolute; top: .35rem; left: .35rem; z-index: 2; min-width: 1.4rem; height: 1.4rem;
  display: grid; place-items: center; border-radius: 999px; background: #fff; color: #111;
  font-size: .72rem; font-weight: 700; padding: 0 .35rem; }
.hle-chip { position: absolute; top: .35rem; right: .35rem; z-index: 2; font-size: .62rem; font-weight: 700;
  text-transform: uppercase; letter-spacing: .04em; padding: .15rem .35rem; border-radius: 999px;
  background: rgba(255,255,255,.85); color: #333; }
.hle-chip.is-pinned { background: var(--theme-success-500); color: #fff; }
.hle-warn { position: absolute; top: 2rem; right: .35rem; z-index: 2; font-size: .62rem; font-weight: 700;
  padding: .15rem .35rem; border-radius: 999px; background: var(--theme-error-500); color: #fff; }
.hle-tile-body { position: absolute; inset: auto 0 0 0; z-index: 2; padding: .45rem .5rem; display: grid; gap: .1rem; }
.hle-tile-title { color: #fff; font-size: .78rem; line-height: 1.25; display: -webkit-box; -webkit-line-clamp: 2;
  -webkit-box-orient: vertical; overflow: hidden; }
.hle-lead .hle-tile-title { font-size: 1rem; -webkit-line-clamp: 3; }
.hle-tile-meta { color: rgba(255,255,255,.75); font-size: .62rem; text-transform: uppercase; letter-spacing: .04em; }
.hle-acts { position: absolute; bottom: .4rem; right: .4rem; z-index: 3; display: flex; gap: .2rem; opacity: 0;
  transition: opacity .12s ease; }
.hle-tile:hover .hle-acts, .hle-tile:focus-within .hle-acts { opacity: 1; }
.hle-act { width: 1.5rem; height: 1.5rem; display: grid; place-items: center; cursor: pointer; font-size: .75rem;
  border-radius: var(--style-radius-s); border: none; background: rgba(255,255,255,.9); color: #111; }
.hle-act:hover:not(:disabled) { background: #fff; }
.hle-act:disabled { opacity: .35; cursor: default; }

.hle-aside { border: 1px solid var(--theme-elevation-150); border-radius: var(--style-radius-s);
  padding: .6rem; display: grid; gap: .5rem; align-content: start; }
.hle-search { width: 100%; font-size: .8rem; padding: .35rem .5rem; border-radius: var(--style-radius-s);
  border: 1px solid var(--theme-elevation-250); background: var(--theme-input-bg, var(--theme-elevation-0));
  color: var(--theme-elevation-800); }
.hle-sr { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); }
.hle-list { list-style: none; margin: 0; padding: 0; display: grid; gap: .25rem; max-height: 34rem; overflow-y: auto; }
.hle-row { display: grid; grid-template-columns: 3.5rem minmax(0, 1fr) auto; gap: .5rem; align-items: center;
  padding: .3rem; border-radius: var(--style-radius-s); border: 1px solid transparent; cursor: grab; }
.hle-row:hover { background: var(--theme-elevation-50); }
.hle-row:focus-visible { outline: 2px solid var(--theme-success-500); outline-offset: 1px; }
.hle-row.is-pending { border-color: var(--theme-success-500); background: var(--theme-success-50); }
.hle-row-thumb { width: 3.5rem; height: 2.3rem; object-fit: cover; border-radius: 3px;
  background: var(--theme-elevation-150); display: block; }
.hle-row-text { display: grid; gap: .1rem; min-width: 0; }
.hle-row-title { font-size: .78rem; line-height: 1.25; display: -webkit-box; -webkit-line-clamp: 2;
  -webkit-box-orient: vertical; overflow: hidden; }
.hle-row-meta { font-size: .68rem; color: var(--theme-elevation-500); }
.hle-row-box { font-size: .65rem; font-weight: 700; text-transform: uppercase; letter-spacing: .04em;
  padding: .15rem .4rem; border-radius: 999px; white-space: nowrap;
  background: var(--theme-elevation-100); color: var(--theme-elevation-600); }
.hle-row.is-live .hle-row-box { background: var(--theme-elevation-800); color: var(--theme-elevation-0); }
.hle-row .hle-row-box.is-pinned { background: var(--theme-success-500); color: #fff; }
.hle-foot { margin: 0; font-size: .7rem; line-height: 1.45; color: var(--theme-elevation-500); }
`
